import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import appSelectors from '@/redux/app/appSelectors';
import userSelectors from '@/redux/user/userSelectors';

import { setDisplayMenu } from '@/redux/app/appSlice';

import RouteNames from '@/router/routes';

import DashboardIcon from '@/images/nav_icons/dashboard.svg?react';
import MyAccountsIcon from '@/images/nav_icons/my-accounts.svg?react';
import PaymentsIcon from '@/images/nav_icons/payments.svg?react';
import PartnerIcon from '@/images/nav_icons/partner.svg?react';
import LoginMtWebIcon from '@/images/nav_icons/login-mt-web.svg?react';
import LoginNeotradeIcon from '@/images/nav_icons/login-neotrade.svg?react';
import DownloadTPIcon from '@/images/nav_icons/download-trade-platform.svg?react';
import PammServiceIcon from '@/images/nav_icons/pamm-service.svg?react';
import ArrowIcon from '@/images/nav_icons/chevron-right.svg?react';

import s from './nav.module.css';

const personalAccountsLink = `/${RouteNames.MY_ACCOUNTS}/${RouteNames.PERSONAL_ACCOUNTS}`;
const tradeAccountsLink = `/${RouteNames.MY_ACCOUNTS}/${RouteNames.TRADE_ACCOUNTS}`;
const demoAccountsLink = `/${RouteNames.MY_ACCOUNTS}/${RouteNames.DEMO_ACCOUNTS}`;

const depositLink = `/${RouteNames.PAYMENTS}/${RouteNames.DEPOSIT}`;
const withdrawLink = `/${RouteNames.PAYMENTS}/${RouteNames.WITHDRAWAL}`;
const transferLink = `/${RouteNames.PAYMENTS}/${RouteNames.TRANSFER}`;
// const paymentHistoryLink = `/${RouteNames.PAYMENTS}/${RouteNames.PAYMENT_HISTORY}`;

const partnerStatisticLink = `/${RouteNames.PARTNERS}/${RouteNames.STATISTIC}`;
const partnerTransactionsLink = `/${RouteNames.PARTNERS}/${RouteNames.TRANSACTIONS}`;
const partnerCommissionsLink = `/${RouteNames.PARTNERS}/${RouteNames.COMMISSIONS}`;

const tradingPlatformsLink = RouteNames.TRADING_PLATFORMS;
const pammLink = RouteNames.PAMM;
const supportLink = `/${RouteNames.SUPPORT}`;

const MT_WEB_URL = import.meta.env.VITE_MT_WEB_URL;
const NEOTRADE_WEB_URL = import.meta.env.VITE_NEOTRADE_WEB_URL;

const navlinkStyles = ({ isActive }) =>
  isActive ? s.navlink_active : s.navlink;

function Nav() {
  const dispatch = useDispatch();

  const i18n = useSelector(appSelectors.translation);
  // const counter = useSelector(supportSelectors.countUnreadMessages);
  const userStatus = useSelector(userSelectors.status);

  const { pathname } = useLocation();

  const [displaySubmenu, setDisplaySubmenu] = useState('');

  const hideMenu = () => dispatch(setDisplayMenu(false));

  const handleClickMyAccounts = () =>
    setDisplaySubmenu(displaySubmenu === 'my-accounts' ? '' : 'my-accounts');

  const handleClickPayments = () =>
    setDisplaySubmenu(displaySubmenu === 'payments' ? '' : 'payments');

  const handleClickPartners = () =>
    setDisplaySubmenu(displaySubmenu === 'partners' ? '' : 'partners');

  useEffect(() => {
    if (
      pathname.includes(RouteNames.PERSONAL_ACCOUNTS) ||
      pathname.includes(RouteNames.TRADE_ACCOUNTS) ||
      pathname.includes(RouteNames.DEMO_ACCOUNTS)
    ) {
      setDisplaySubmenu('my-accounts');
      return;
    }

    if (
      pathname.includes(RouteNames.DEPOSIT) ||
      pathname.includes(RouteNames.WITHDRAWAL) ||
      pathname.includes(RouteNames.TRANSFER) ||
      pathname.includes(RouteNames.PAYMENT_HISTORY)
    ) {
      setDisplaySubmenu('payments');
      return;
    }

    if (
      pathname.includes(RouteNames.STATISTIC) ||
      pathname.includes(RouteNames.COMMISSIONS) ||
      pathname.includes(RouteNames.TRANSACTIONS)
    ) {
      setDisplaySubmenu('partners');
      return;
    }

    setDisplaySubmenu('');
  }, [pathname]);

  const accountsArrowStyles =
    displaySubmenu === 'my-accounts' ? s.arrow_down : s.arrow;

  const paymentsArrowStyles =
    displaySubmenu === 'payments' ? s.arrow_down : s.arrow;

  const partnersArrowStyles =
    displaySubmenu === 'partners' ? s.arrow_down : s.arrow;

  const accountsSubmenuStyles =
    displaySubmenu === 'my-accounts' ? s.submenu : s.submenu_hidden;

  const paymentsSubmenuStyles =
    displaySubmenu === 'payments' ? s.submenu : s.submenu_hidden;

  const partnersSubmenuStyles =
    displaySubmenu === 'partners' ? s.submenu : s.submenu_hidden;

  return (
    <nav className={s.nav}>
      {/* Main */}
      <div className={s.nav_group}>
        <p className={s.nav_group_title}>{i18n.Main || 'Main'}</p>

        {/* Dashboard */}
        <NavLink to="/" onClick={hideMenu} className={navlinkStyles}>
          <DashboardIcon stroke="currentColor" className={s.icon} />
          <span>{i18n.Dashboard || 'Dashboard'}</span>
        </NavLink>

        {/* My accounts */}
        <div className={s.btn_submenu_wrapper}>
          <button
            type="button"
            onClick={handleClickMyAccounts}
            className={s.navlink}
          >
            <MyAccountsIcon stroke="currentColor" className={s.icon} />
            <span>{i18n['My accounts']}</span>
            <ArrowIcon className={accountsArrowStyles} fill="currentColor" />
          </button>
          <ul className={accountsSubmenuStyles}>
            <li>
              <NavLink
                to={personalAccountsLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Personal accounts']}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={tradeAccountsLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Trade accounts']}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={demoAccountsLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Demo accounts']}
              </NavLink>
            </li>
          </ul>
        </div>
      </div>

      {/* Money */}
      <div className={s.nav_group}>
        <p className={s.nav_group_title}>{i18n.Money || 'Money'}</p>

        {/* Payments */}
        <div className={s.btn_submenu_wrapper}>
          <button
            type="button"
            onClick={handleClickPayments}
            className={s.navlink}
          >
            <PaymentsIcon stroke="currentColor" className={s.icon} />
            <span>{i18n.Payments}</span>
            <ArrowIcon className={paymentsArrowStyles} fill="currentColor" />
          </button>
          <ul className={paymentsSubmenuStyles}>
            <li>
              <NavLink
                to={userStatus === 'legal_entity' ? supportLink : depositLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Deposit']}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={userStatus === 'legal_entity' ? supportLink : withdrawLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Withdrawal']}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={transferLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Internal transfer']}
              </NavLink>
            </li>
            {/* <li>
              <NavLink
                to={paymentHistoryLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n['Payment history']}
              </NavLink>
            </li> */}
          </ul>
        </div>

        {/* Partners */}
        <div className={s.btn_submenu_wrapper}>
          <button
            type="button"
            onClick={handleClickPartners}
            className={s.navlink}
          >
            <PartnerIcon stroke="currentColor" className={s.icon} />
            <span>{i18n.Partner}</span>
            <ArrowIcon className={partnersArrowStyles} fill="currentColor" />
          </button>
          <ul className={partnersSubmenuStyles}>
            <li>
              <NavLink
                to={partnerStatisticLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n.Statistic}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={partnerTransactionsLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n.Transactions}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={partnerCommissionsLink}
                onClick={hideMenu}
                className={navlinkStyles}
              >
                {i18n.Commissions}
              </NavLink>
            </li>
          </ul>
        </div>
      </div>

      {/* Trading */}
      <div className={s.nav_group}>
        <p className={s.nav_group_title}>{i18n.Trading || 'Trading'}</p>
        {/* Login MT-Web */}
        <a
          href={MT_WEB_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={s.navlink}
        >
          <LoginMtWebIcon fill="currentColor" className={s.icon} />
          <span>{i18n['Login MT-Web'] || 'Login MT-Web'}</span>
        </a>
        {/* Login Neotrade */}
        <a
          href={NEOTRADE_WEB_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={s.navlink}
        >
          <LoginNeotradeIcon fill="currentColor" className={s.icon} />
          <span>{i18n['Login Neotrade'] || 'Login Neotrade'}</span>
        </a>
        {/* Download Trade Platform */}
        <NavLink
          to={tradingPlatformsLink}
          onClick={hideMenu}
          className={navlinkStyles}
        >
          <DownloadTPIcon stroke="currentColor" className={s.icon} />
          <span>
            {i18n['Download Trade Platform'] || 'Download Trade Platform'}
          </span>
        </NavLink>
      </div>

      {/* Investments */}
      <div className={s.nav_group}>
        <p className={s.nav_group_title}>{i18n.Investments || 'Investments'}</p>

        <NavLink to={pammLink} onClick={hideMenu} className={navlinkStyles}>
          <PammServiceIcon stroke="currentColor" className={s.icon} />
          <span>{i18n['PAMM service']}</span>
        </NavLink>
      </div>
    </nav>
  );
}

export default Nav;
